import React, { useState, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import ApiClient from '../../utils/ApiClient';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

function BuyboxWinRateReport() {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sellers, setSellers] = useState([]);
    const [selectedSellers, setSelectedSellers] = useState(new Set());
    const [chartData, setChartData] = useState(null);
    const [filters, setFilters] = useState({
        startDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        endDate: new Date().toISOString().split('T')[0]
    });
    const [searchTerm, setSearchTerm] = useState('');
    const [saveSuccess, setSaveSuccess] = useState(false);
    const [loadingSelection, setLoadingSelection] = useState(false);
    const [sharedOffersOnly, setSharedOffersOnly] = useState(false);
    const [showUniqueAsins, setShowUniqueAsins] = useState(true);
    const [rawApiData, setRawApiData] = useState([]);
    const [fulfillmentType, setFulfillmentType] = useState('all'); // 'all', 'fba', 'merchant'
    const [showInventory, setShowInventory] = useState(false);
    const [inventoryData, setInventoryData] = useState([]);

    useEffect(() => {
        fetchSellers();
        loadSavedSelection();
    }, []);

    // Regenerate chart when showUniqueAsins or showInventory changes
    useEffect(() => {
        if (rawApiData.length > 0) {
            setChartData(generateChartData(rawApiData));
        }
    }, [showUniqueAsins, showInventory, inventoryData]);

    useEffect(() => {
        if (selectedSellers.size > 0) {
            fetchChartData();
        }
    }, [selectedSellers, filters, sharedOffersOnly, fulfillmentType]);

    const fetchSellers = async () => {
        try {
            setLoading(true);
            const response = await ApiClient.post('/api/buybox-report-sellers', {});
            setSellers(response.data || []);
        } catch (err) {
            setError('Failed to load sellers');
            console.error('Error fetching sellers:', err);
        } finally {
            setLoading(false);
        }
    };

    const fetchChartData = async () => {
        try {
            setLoading(true);
            const requestData = {
                startDate: filters.startDate,
                endDate: filters.endDate,
                sellerIds: Array.from(selectedSellers),
                sharedOffersOnly: sharedOffersOnly,
                fulfillmentType: fulfillmentType
            };

            // Fetch buybox data and inventory data in parallel
            const [buyboxResponse, inventoryResponse] = await Promise.all([
                ApiClient.post('/api/buybox-report-data', requestData),
                ApiClient.post('/api/inventory-daily', {
                    startDate: filters.startDate,
                    endDate: filters.endDate
                })
            ]);

            const data = buyboxResponse.data || [];
            const invData = inventoryResponse.data || [];

            setRawApiData(data);
            setInventoryData(invData);
            setChartData(generateChartData(data));
        } catch (err) {
            setError('Failed to load chart data');
            console.error('Error fetching chart data:', err);
        } finally {
            setLoading(false);
        }
    };

    const generateChartData = (apiData) => {
        const sellerColors = [
            '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
            '#EC4899', '#06B6D4', '#84CC16', '#F97316', '#6366F1',
            '#14B8A6', '#F43F5E'
        ];

        // Group by seller name instead of ID to handle multiple IDs per seller
        const sellerNameMap = {};
        const selectedSellerNames = new Set();

        sellers.forEach((seller, index) => {
            if (selectedSellers.has(seller.id)) {
                selectedSellerNames.add(seller.name);
                if (!sellerNameMap[seller.name]) {
                    sellerNameMap[seller.name] = {
                        name: seller.name,
                        color: sellerColors[index % sellerColors.length],
                        buyboxData: {},
                        listingsData: {}
                    };
                }
            }
        });

        // Aggregate data by seller name and date
        apiData.forEach(item => {
            if (selectedSellerNames.has(item.sellerName)) {
                const date = item.date;
                const name = item.sellerName;

                if (!sellerNameMap[name].buyboxData[date]) {
                    sellerNameMap[name].buyboxData[date] = 0;
                    sellerNameMap[name].listingsData[date] = 0;
                }
                sellerNameMap[name].buyboxData[date] += item.buyboxWins;
                sellerNameMap[name].listingsData[date] += (item.totalListings || 0);
            }
        });

        const allDates = [...new Set(apiData.map(item => item.date))].sort();

        const datasets = [];

        // Add buybox wins lines (solid)
        Object.values(sellerNameMap).forEach(seller => {
            datasets.push({
                label: `${seller.name} (Buybox Wins)`,
                data: allDates.map(date => seller.buyboxData[date] || 0),
                borderColor: seller.color,
                backgroundColor: seller.color + '20',
                tension: 0.1,
                borderWidth: 2
            });
        });

        // Add unique ASINs lines (dashed) - only if showUniqueAsins is true
        if (showUniqueAsins) {
            Object.values(sellerNameMap).forEach(seller => {
                datasets.push({
                    label: `${seller.name} (Unique ASINs)`,
                    data: allDates.map(date => seller.listingsData[date] || 0),
                    borderColor: seller.color,
                    backgroundColor: 'transparent',
                    tension: 0.1,
                    borderWidth: 1,
                    borderDash: [5, 5]
                });
            });
        }

        // Add inventory line if enabled
        if (showInventory && inventoryData.length > 0) {
            // Create a map of date -> inventory units
            const inventoryByDate = {};
            inventoryData.forEach(item => {
                inventoryByDate[item.date] = item.totalUnits;
            });

            datasets.push({
                label: 'My FBA Inventory (Units)',
                data: allDates.map(date => inventoryByDate[date] || null),
                borderColor: '#000000',
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
                tension: 0.1,
                borderWidth: 2,
                borderDash: [10, 5],
                yAxisID: 'y1'
            });
        }

        return {
            labels: allDates,
            datasets
        };
    };

    const handleDateRangeClick = (days) => {
        const endDate = new Date();
        const startDate = new Date(endDate.getTime() - days * 24 * 60 * 60 * 1000);

        setFilters({
            startDate: startDate.toISOString().split('T')[0],
            endDate: endDate.toISOString().split('T')[0]
        });
    };

    const handleInputChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleSellerToggle = (sellerId) => {
        setSelectedSellers(prev => {
            const newSet = new Set(prev);
            const seller = sellers.find(s => s.id === sellerId);

            if (seller) {
                // Find all IDs for this seller name
                const allIdsForName = sellers
                    .filter(s => s.name === seller.name)
                    .map(s => s.id);

                if (newSet.has(sellerId)) {
                    // Remove all IDs for this seller name
                    allIdsForName.forEach(id => newSet.delete(id));
                } else {
                    // Add all IDs for this seller name
                    allIdsForName.forEach(id => newSet.add(id));
                }
            }

            return newSet;
        });
    };

    const handleSelectAll = () => {
        const filteredSellerIds = filteredSellerNames.flatMap(name =>
            sellersByName[name].map(seller => seller.id)
        );

        const allSelected = filteredSellerIds.every(id => selectedSellers.has(id));

        if (allSelected) {
            setSelectedSellers(new Set());
        } else {
            setSelectedSellers(new Set(filteredSellerIds));
        }
    };

    const saveCurrentSelection = async () => {
        try {
            const selectionData = {
                buyboxWinRateReport: {
                    selectedSellerIds: Array.from(selectedSellers),
                    filters: filters,
                    savedAt: new Date().toISOString()
                }
            };

            const response = await ApiClient.post('/api/user/settings', {
                settings: selectionData
            });

            if (response.status === 200) {
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 3000);
            }
        } catch (err) {
            console.error('Error saving selection:', err);
            // Fallback to localStorage
            const selectionData = {
                selectedSellerIds: Array.from(selectedSellers),
                filters: filters,
                savedAt: new Date().toISOString()
            };
            localStorage.setItem('buyboxWinRateSelection', JSON.stringify(selectionData));
            setSaveSuccess(true);
            setTimeout(() => setSaveSuccess(false), 3000);
        }
    };

    const loadSavedSelection = async () => {
        setLoadingSelection(true);
        try {
            const response = await ApiClient.get('/api/user/settings');
            const settings = response.data?.settings;

            if (settings?.buyboxWinRateReport) {
                const saved = settings.buyboxWinRateReport;

                if (saved.selectedSellerIds && saved.selectedSellerIds.length > 0) {
                    setSelectedSellers(new Set(saved.selectedSellerIds));
                }

                if (saved.filters) {
                    setFilters(saved.filters);
                }
                return true;
            }
        } catch (err) {
            console.error('Error loading saved selection from API:', err);
            // Fallback to localStorage
            try {
                const saved = localStorage.getItem('buyboxWinRateSelection');
                if (saved) {
                    const data = JSON.parse(saved);

                    if (data.selectedSellerIds && data.selectedSellerIds.length > 0) {
                        setSelectedSellers(new Set(data.selectedSellerIds));
                    }

                    if (data.filters) {
                        setFilters(data.filters);
                    }
                    return true;
                }
            } catch (localErr) {
                console.error('Error loading saved selection from localStorage:', localErr);
            }
        } finally {
            setLoadingSelection(false);
        }
        return false;
    };

    // Group sellers by name for display, but keep all IDs for selection
    const sellersByName = {};
    sellers.forEach(seller => {
        if (!sellersByName[seller.name]) {
            sellersByName[seller.name] = [];
        }
        sellersByName[seller.name].push(seller);
    });

    const filteredSellerNames = Object.keys(sellersByName).filter(name =>
        name.toLowerCase().includes(searchTerm.toLowerCase())
    ).sort();

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
            },
            title: {
                display: true,
                text: 'Daily Buybox Wins by Seller'
            }
        },
        elements: {
            point: {
                radius: 0,
                hoverRadius: 6
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Buybox Wins'
                }
            },
            y1: {
                beginAtZero: true,
                position: 'right',
                display: showInventory,
                title: {
                    display: true,
                    text: 'Inventory Units'
                },
                grid: {
                    drawOnChartArea: false
                }
            },
            x: {
                title: {
                    display: true,
                    text: 'Date'
                },
                ticks: {
                    callback: function(value, index, values) {
                        const date = new Date(this.getLabelForValue(value));
                        return date.toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric'
                        });
                    }
                }
            }
        }
    };

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="text-red-600 mb-4">{error}</div>
                <button
                    onClick={() => window.location.reload()}
                    className="text-blue-600 hover:text-blue-800"
                >
                    Try Again
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-lg shadow">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">
                    Track daily Buybox wins by seller over time
                </h2>

                {/* Filters Section */}
                <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Filters</h3>

                    {/* Quick Date Ranges */}
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Quick Date Ranges
                        </label>
                        <div className="flex flex-wrap gap-2">
                            {[
                                { label: 'Last 7 Days', days: 7 },
                                { label: 'Last 30 Days', days: 30 },
                                { label: 'Last 90 Days', days: 90 },
                                { label: 'Last Year', days: 365 }
                            ].map(range => (
                                <button
                                    key={range.days}
                                    onClick={() => handleDateRangeClick(range.days)}
                                    className="px-3 py-1 text-sm bg-gray-100 hover:bg-gray-200 rounded-md"
                                >
                                    {range.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Custom Date Range */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Start Date
                            </label>
                            <input
                                type="date"
                                value={filters.startDate}
                                onChange={(e) => handleInputChange('startDate', e.target.value)}
                                className="mt-1 block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                End Date
                            </label>
                            <input
                                type="date"
                                value={filters.endDate}
                                onChange={(e) => handleInputChange('endDate', e.target.value)}
                                className="mt-1 block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                            />
                        </div>
                    </div>

                    {/* Shared Offers Filter */}
                    <div className="mt-4 flex flex-wrap gap-6">
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={sharedOffersOnly}
                                onChange={(e) => setSharedOffersOnly(e.target.checked)}
                                className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Shared offers only
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                                (Only show ASINs where all selected sellers compete)
                            </span>
                        </label>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={showUniqueAsins}
                                onChange={(e) => setShowUniqueAsins(e.target.checked)}
                                className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Show Unique ASINs
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                                (Display dashed line for total unique ASINs per seller)
                            </span>
                        </label>
                        <label className="flex items-center">
                            <input
                                type="checkbox"
                                checked={showInventory}
                                onChange={(e) => setShowInventory(e.target.checked)}
                                className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                            />
                            <span className="text-sm font-medium text-gray-700">
                                Show My FBA Inventory
                            </span>
                            <span className="ml-2 text-xs text-gray-500">
                                (Display inventory units on secondary axis)
                            </span>
                        </label>
                    </div>

                    {/* Fulfillment Type Filter */}
                    <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Fulfillment Type
                        </label>
                        <div className="flex gap-4">
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="fulfillmentType"
                                    value="all"
                                    checked={fulfillmentType === 'all'}
                                    onChange={(e) => setFulfillmentType(e.target.value)}
                                    className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                />
                                <span className="text-sm text-gray-700">All</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="fulfillmentType"
                                    value="fba"
                                    checked={fulfillmentType === 'fba'}
                                    onChange={(e) => setFulfillmentType(e.target.value)}
                                    className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                />
                                <span className="text-sm text-gray-700">FBA Only</span>
                            </label>
                            <label className="flex items-center">
                                <input
                                    type="radio"
                                    name="fulfillmentType"
                                    value="merchant"
                                    checked={fulfillmentType === 'merchant'}
                                    onChange={(e) => setFulfillmentType(e.target.value)}
                                    className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300"
                                />
                                <span className="text-sm text-gray-700">Merchant Fulfilled Only</span>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Select Sellers Section */}
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900">Select Sellers</h3>
                        <button
                            onClick={handleSelectAll}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                        >
                            Select All
                        </button>
                    </div>

                    <div className="mb-4">
                        <input
                            type="text"
                            placeholder="Search sellers..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="block w-full pl-3 pr-3 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        />
                    </div>

                    <div className="text-sm text-gray-600 mb-2">
                        {Object.values(sellersByName).filter(sellers =>
                            sellers.some(seller => selectedSellers.has(seller.id))
                        ).length} of {filteredSellerNames.length} sellers selected
                    </div>

                    <div className="max-h-60 overflow-y-auto border border-gray-200 rounded-md p-3 space-y-2">
                        {filteredSellerNames.map(sellerName => {
                            const sellerGroup = sellersByName[sellerName];
                            const isSelected = sellerGroup.some(seller => selectedSellers.has(seller.id));
                            const representativeId = sellerGroup[0].id;

                            return (
                                <label key={sellerName} className="flex items-center">
                                    <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => handleSellerToggle(representativeId)}
                                        className="mr-2 h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                    />
                                    <span className="text-sm text-gray-700">
                                        {sellerName}
                                        {sellerGroup.length > 1 && (
                                            <span className="text-gray-500 ml-1">({sellerGroup.length} accounts)</span>
                                        )}
                                    </span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Buybox Wins Chart */}
            <div className="bg-white p-6 rounded-lg shadow">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Daily Buybox Wins Chart</h3>
                    <div className="flex items-center gap-4">
                        {saveSuccess && (
                            <span className="text-green-600 text-sm font-medium">
                                Selection saved!
                            </span>
                        )}
                        <button
                            onClick={loadSavedSelection}
                            disabled={loadingSelection}
                            className={`px-4 py-2 text-white text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
                                loadingSelection
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-gray-600 hover:bg-gray-700'
                            }`}
                        >
                            {loadingSelection ? (
                                <div className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Loading...
                                </div>
                            ) : (
                                'Load Selection'
                            )}
                        </button>
                        <button
                            onClick={saveCurrentSelection}
                            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            disabled={selectedSellers.size === 0}
                        >
                            Save Selection
                        </button>
                    </div>
                </div>

                {loading && (
                    <div className="text-center py-12">
                        <div className="text-gray-600">Loading chart data...</div>
                    </div>
                )}

                {!loading && selectedSellers.size === 0 && (
                    <div className="text-center py-12 text-gray-600">
                        Select one or more sellers to view buybox wins data
                    </div>
                )}

                {!loading && selectedSellers.size > 0 && chartData && (
                    <div style={{ height: '600px' }}>
                        <Line data={chartData} options={chartOptions} />
                    </div>
                )}

                {!loading && selectedSellers.size > 0 && !chartData && (
                    <div className="text-center py-12 text-gray-600">
                        No data available for the selected date range and sellers.
                    </div>
                )}
            </div>
        </div>
    );
}

export default BuyboxWinRateReport;
